import React, { useState, useEffect } from 'react';
import { Product, ProductVariant } from '../types';
import { useCart } from '../context/CartContext';
import { X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useLocation } from 'wouter';
import toast from 'react-hot-toast';

interface QuickBuyModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenCart?: () => void;
}

export default function QuickBuyModal({ product, isOpen, onClose, onOpenCart }: QuickBuyModalProps) {
  const [, setLocation] = useLocation();
  const { addToCart } = useCart();

  // Normalize variants
  const defaultVariants: ProductVariant[] = [
    { weight: '100g', price: product?.price || 58, costPrice: 0, image: product?.image || '' },
    { weight: '250g', price: Math.round((product?.price || 58) * 2.3), costPrice: 0, image: product?.image || '' },
    { weight: '500g', price: Math.round((product?.price || 58) * 4.4), costPrice: 0, image: product?.image || '' },
  ];

  const variants = (product?.variants && product.variants.length > 0) 
    ? product.variants 
    : defaultVariants;

  const [selectedWeight, setSelectedWeight] = useState(variants[0]?.weight || '100g');

  useEffect(() => {
    if (product) {
      const initialWeight = product.variants?.[0]?.weight || '100g';
      setSelectedWeight(initialWeight);
    }
  }, [product]);

  if (!isOpen || !product) return null;

  const currentVariant = variants.find(v => v.weight === selectedWeight) || variants[0];
  const displayPrice = currentVariant ? currentVariant.price : product.price;
  const isOutOfStock = product.isOutOfStock === true || product.stock === 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOutOfStock) return;
    
    addToCart(product, selectedWeight, 1, displayPrice);
    toast.success(`Added ${product.name} (${selectedWeight}) to cart!`, {
      icon: '🛒',
      duration: 2500,
      style: {
        background: '#2B2B2B',
        color: '#FFFFFF',
        fontSize: '12px',
        fontWeight: 'bold',
      }
    });

    onClose();
    if (onOpenCart) {
      onOpenCart();
    }
  };

  const handleViewFullDetails = () => {
    onClose();
    setLocation(`/product/${product.id}`);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        {/* Dimmed backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-[2px] cursor-pointer"
        />

        {/* Slide-up bottom sheet */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-50 p-6 pb-8 border border-[#E5E0D8] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-4 right-4 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-black hover:border-black transition-colors"
          >
            <X className="w-4 h-4 stroke-[2.2]" />
          </button>

          {/* Product Header */}
          <div className="pr-8 mb-4">
            <h2 className="text-base sm:text-lg font-bold text-[#1F1F1F] leading-snug">
              {product.name}
            </h2>
            <div className="mt-1 text-base sm:text-lg font-extrabold text-[#1F1F1F]">
              Rs. {displayPrice.toFixed(2)}
            </div>
          </div>

          <div className="h-[1px] bg-[#EBEBEB] w-full my-4" />

          {/* Size / Variant Selector */}
          <div className="mb-6">
            <div className="text-xs font-semibold text-[#444444] mb-2.5">
              Size: <span className="font-bold text-black">{selectedWeight}</span>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {variants.map((v) => {
                const isSelected = selectedWeight === v.weight;
                return (
                  <button
                    key={v.weight}
                    type="button"
                    onClick={() => setSelectedWeight(v.weight)}
                    className={`px-4 py-2 text-xs font-bold transition-all min-w-[58px] text-center rounded-sm ${
                      isSelected
                        ? 'variant-box-active ring-1 ring-black shadow-sm'
                        : 'variant-box-inactive hover:border-black'
                    }`}
                  >
                    {v.weight}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Add to Cart CTA */}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`w-full py-3.5 px-6 rounded font-bold text-sm tracking-wide transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 ${
              isOutOfStock
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'btn-terracotta bg-[#D97736] hover:bg-[#C85A17] text-white'
            }`}
          >
            {isOutOfStock ? 'Sold out' : 'Add to cart'}
          </button>

          {/* View full details link */}
          <div className="mt-3.5 text-center">
            <button
              type="button"
              onClick={handleViewFullDetails}
              className="text-xs font-medium text-[#555555] underline hover:text-[#D97736] transition-colors"
            >
              View full details
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
